import { Outlet, Link, useRouterState } from '@tanstack/react-router';
import {
  AppShell,
  Badge,
  Box,
  Button,
  ColorModeToggle,
  Icon,
  IconButton,
  type NavItem,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { readCurrentRole } from '../api/client';
import { meControllerGetNotifications } from '../api/generated/me/me';
import { getReadIds, subscribeReadState } from '../notifications/readState';
import { useFarmerSurfaces } from '../api/useFarmerSurfaces';

function signOut() {
  // Clear tokens then hard-navigate; hard nav guarantees the React tree
  // tears down (no stale TanStack Query cache bleed-through into the next
  // session).
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('fse_access_token');
    window.localStorage.removeItem('fse_refresh_token');
    window.location.assign('/login');
  }
}

/**
 * App-bar notifications bell with a live unread badge.
 *
 * Fetches the first page of /me/notifications and counts the rows NOT in the
 * per-device read set (the same localStorage state the inbox writes). Subscribes
 * to read-state changes so the count clears the instant you read a row or hit
 * "Mark all as read", and shares the inbox's exact query key so the two stay in
 * lockstep (one request, one cache). Badge hides at 0, caps at 9+. Visible to
 * every authenticated role; a role with no notifications simply shows no badge.
 */
function NotificationsBell() {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['me-notifications', { limit: 30, offset: 0 }],
    queryFn: ({ signal }) => meControllerGetNotifications({ limit: 30, offset: 0 }, signal),
    staleTime: 30_000,
  });
  const [readIds, setReadIds] = useState<Set<string>>(() => getReadIds());
  useEffect(() => subscribeReadState(() => setReadIds(getReadIds())), []);

  const rows = query.data?.data ?? [];
  const unread = rows.reduce((n, r) => (readIds.has(r.id) ? n : n + 1), 0);

  return (
    <Link to="/notifications" style={{ display: 'inline-flex', color: 'inherit' }}>
      <IconButton size="small" color="inherit" aria-label={t('nav.notifications')}>
        <Badge count={unread} max={9}>
          <Icon name="notifications" size={22} color="inherit" />
        </Badge>
      </IconButton>
    </Link>
  );
}

interface NavSpec extends Omit<NavItem, 'label'> {
  /** i18n key (TD-072) — resolved through t() at render so labels follow the
   *  chosen language. The locale namespace lives in src/i18n/locales/*.json. */
  labelKey: string;
  /** Roles allowed to see this item. Undefined = visible to all authed users. */
  roles?: Array<'admin' | 'coop_admin' | 'farmer' | 'group_lead' | 'buyer'>;
  /**
   * Data-aware visibility gate (via useFarmerSurfaces). Pilot scope: the only
   * gate is `contributions`, which is always on for farmer-like roles (pilot
   * farmers are coop members; independent harvests were cut). Undefined = role
   * gating only.
   */
  gate?: 'contributions';
  /** i18n key for the sidebar section cluster this item belongs to (task 5).
   *  Resolved through t() at render; AppShell renders a subheader when the
   *  section changes between consecutive (post-filter) items. */
  sectionKey?: string;
}

// The sidebar is the single source of truth for navigation (the home grid is
// now a light dashboard, account items live under Settings). Items are listed
// so that, AFTER the role + gate filter, each role's `sectionKey` clusters stay
// contiguous: coop_admin/admin items and farmer/group_lead items are mutually
// exclusive by `roles`, so interleaving them here is safe. Notifications and
// Settings are shared tails. `gate`d items only appear for the farmer role, so
// they never split a cluster for other roles.
const navItems: NavSpec[] = [
  // --- Overview ---------------------------------------------------------
  // Dashboard + Farmers are scoped to admin/coop_admin: GET /farmers is
  // @Roles('admin','coop_admin') (403s farmers) and the dashboard is
  // coop-centric, so farmers get their own /home instead.
  { labelKey: 'nav.dashboard', href: '/dashboard', roles: ['admin', 'coop_admin'], sectionKey: 'nav.section.overview' },
  // Farmer / group_lead landing.
  { labelKey: 'nav.home', href: '/home', roles: ['farmer', 'group_lead'], sectionKey: 'nav.section.overview' },

  // --- Cooperative (coop-admin / admin) ---------------------------------
  { labelKey: 'nav.farmers', href: '/farmers', roles: ['admin', 'coop_admin'], sectionKey: 'nav.section.cooperative' },
  // Coop admin / admin only — the batches controller is @Roles('admin','coop_admin')
  // and farmers have no batch-management surface, so the link is hidden for them.
  { labelKey: 'nav.batches', href: '/batches', roles: ['admin', 'coop_admin'], sectionKey: 'nav.section.cooperative' },
  // Phase 3 — Orders (coop-admin incoming buyer-demand inbox; label de-jargoned
  // from "Demands" → "Orders", task 7 — route path /demands is unchanged).
  { labelKey: 'nav.demands', href: '/demands', roles: ['admin', 'coop_admin'], sectionKey: 'nav.section.cooperative' },
  // Market = indicative price view, shown to coop_admin here under Cooperative
  // (farmers get it under their own Market section below).
  { labelKey: 'nav.market', href: '/market', roles: ['coop_admin'], sectionKey: 'nav.section.cooperative' },

  // --- Approvals (coop-admin / admin) ------------------------------------
  // Pilot scope: informal groups (G2) were cut, so group_lead no longer gets the
  // group farm-edit review queue.
  { labelKey: 'nav.pendingReviews', href: '/pending-reviews', roles: ['admin', 'coop_admin'], sectionKey: 'nav.section.approvals' },
  // Tier-1 KYC review queue (coop-vouched verification). Server scopes coop_admin
  // to their coop's submitted KYC; admin sees all. 403s everyone else.
  { labelKey: 'nav.kycReviews', href: '/kyc-reviews', roles: ['admin', 'coop_admin'], sectionKey: 'nav.section.approvals' },

  // --- My farm (coop-member farmer surface) ------------------------------
  // Backed by self-scoped / JwtAuthGuard-only endpoints (/me/*, self
  // /contributions, /kyc/me). group_lead is "fundamentally a farmer" (mirrors
  // mobile jwt.ts) and shares this surface.
  { labelKey: 'nav.myFarm', href: '/my-farm', roles: ['farmer', 'group_lead'], sectionKey: 'nav.section.myFarm' },
  { labelKey: 'nav.myContributions', href: '/my-contributions', roles: ['farmer', 'group_lead'], gate: 'contributions', sectionKey: 'nav.section.myFarm' },
  { labelKey: 'nav.verification', href: '/verification', roles: ['farmer', 'group_lead'], sectionKey: 'nav.section.myFarm' },

  // --- Market (farmer / group_lead) -------------------------------------
  { labelKey: 'nav.market', href: '/market', roles: ['farmer', 'group_lead'], sectionKey: 'nav.section.market' },
  // Market-price dashboard (#1b) — indicative low/high/latest + 30-day sparkline.
  // The underlying /market/prices endpoints are PUBLIC, so this is broad: visible
  // to every operational role (no `roles`). Sits in the Market section.
  { labelKey: 'nav.priceDashboard', href: '/price-dashboard', sectionKey: 'nav.section.market' },
  // Rewards / incentives wallet (#4) — points balance + ledger. Farmer-scoped
  // (group_lead is a farmer too) — they earn the points the screen tallies.
  { labelKey: 'nav.incentives', href: '/incentives', roles: ['farmer', 'group_lead'], sectionKey: 'nav.section.market' },

  // --- Marketplace (Phase 5 transactional layer) ------------------------
  // Buyer surface: browse listings + own offers + KYC. Seller surface (coop_admin
  // sells batches): my listings. Pilot scope: harvest-sourced listings were cut,
  // so farmers/group_leads have no listing surface. "My orders"
  // (the transactional /orders) is shared by every party — labelled distinctly
  // from the coop-admin "Orders" buyer-demand inbox (/demands) above. Buyer-only
  // and seller-only items are mutually exclusive by role, so each role sees one
  // contiguous Marketplace cluster.
  { labelKey: 'nav.marketplace', href: '/marketplace', roles: ['buyer'], sectionKey: 'nav.section.marketplace' },
  // Buyer demand form (#1a) — a verified buyer posts a "want to buy" demand.
  // Buyer-scoped; the screen itself gates on buyer KYC eligibility.
  { labelKey: 'nav.createDemand', href: '/create-demand', roles: ['buyer'], sectionKey: 'nav.section.marketplace' },
  // Client-only buyer Watchlist (Builder D). Also reachable from the marketplace
  // header shortcut; this gives it a durable, role-scoped sidebar home.
  { labelKey: 'nav.watchlist', href: '/watchlist', roles: ['buyer'], sectionKey: 'nav.section.marketplace' },
  { labelKey: 'nav.myOffers', href: '/my-offers', roles: ['buyer'], sectionKey: 'nav.section.marketplace' },
  { labelKey: 'nav.buyerVerification', href: '/buyer-verification', roles: ['buyer'], sectionKey: 'nav.section.marketplace' },
  { labelKey: 'nav.myListings', href: '/my-listings', roles: ['admin', 'coop_admin'], sectionKey: 'nav.section.marketplace' },
  // Buyer-demand inbox for SELLERS (demand-loop). Reuses the /demands screen (the
  // coop-admin sees the same page under "Orders" in the Cooperative section
  // above); here it gives farmers/group_leads a discoverable place to find a
  // demand for produce they supply and tap "Respond". coop_admin already reaches
  // it via the Cooperative "Orders" link, so it is NOT re-listed for them here.
  { labelKey: 'nav.buyerDemands', href: '/demands', roles: ['farmer', 'group_lead'], sectionKey: 'nav.section.marketplace' },
  { labelKey: 'nav.myOrders', href: '/orders', roles: ['admin', 'coop_admin', 'farmer', 'group_lead', 'buyer'], sectionKey: 'nav.section.marketplace' },

  // --- Notifications (all operational roles) — its own durable entry, plus
  //     the app-bar bell (task 6). Placed before Account as a shared tail.
  { labelKey: 'nav.notifications', href: '/notifications', sectionKey: 'nav.section.notifications' },

  // --- Account (shared) — Settings is the single account hub; Change PIN,
  //     My data and Privacy now live UNDER Settings (task 2/3), not here.
  // Buyer Account hub (#9): the buyer's stat grid (orders / spent / watchlist)
  // + quick links. Buyer-scoped, sits above Settings in the shared Account tail.
  { labelKey: 'nav.buyerAccount', href: '/buyer-account', roles: ['buyer'], sectionKey: 'nav.section.account' },
  { labelKey: 'nav.settings', href: '/settings', sectionKey: 'nav.section.account' },
  // Kanban is intentionally NOT in this sidebar — it lives under the sysCollab
  // role at /kanban.html and the operational personas (coop_admin / farmer /
  // etc.) shouldn't see it. The router's beforeLoad guard on this layout
  // also redirects sysCollab away from /_app/* so they can't land here.
];

/**
 * Active-state matcher. A nav item is active when the current path is the item
 * href exactly, OR the current path is a descendant route (href + '/...'), so a
 * detail page keeps its parent link highlighted (e.g. /orders/:id lights
 * up My orders, /farmers/:id lights up Farmers). `/home` matches exactly only
 * (it has no descendant routes and shouldn't catch anything else). There is no
 * '/' nav item here, so no root-prefix pitfall.
 */
function isNavActive(currentPath: string, href: string): boolean {
  if (href === '/home') return currentPath === '/home';
  return currentPath === href || currentPath.startsWith(`${href}/`);
}

export function AppLayout() {
  const { t } = useTranslation();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const role = readCurrentRole();

  // Data-aware farmer surfaces (My contributions). Only engaged for farmer-like
  // roles — passing `enabled=false` for other roles skips the farmer-scoped
  // probes (those nav items are role-filtered out anyway).
  const isFarmerLike = role === 'farmer' || role === 'group_lead';
  const surfaces = useFarmerSurfaces(isFarmerLike);

  const nav: NavItem[] = navItems
    .filter((item) => !item.roles || (role !== null && item.roles.includes(role as 'admin' | 'coop_admin' | 'farmer' | 'group_lead' | 'buyer')))
    .filter((item) => {
      if (item.gate === 'contributions') return surfaces.showContributions;
      return true;
    })
    .map((item) => ({
      label: t(item.labelKey),
      href: item.href,
      active: isNavActive(currentPath, item.href),
      section: item.sectionKey ? t(item.sectionKey) : undefined,
    }));

  return (
    <AppShell
      title="Food Security Exchange"
      nav={nav}
      appBarRight={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Durable notifications entry — visible to all operational roles
              (task 6). Bell carries a live unread badge (per-device read state). */}
          <NotificationsBell />
          <ColorModeToggle />
          <Button variant="text" size="small" onClick={signOut}>
            {t('nav.signOut')}
          </Button>
        </Box>
      }
      renderLink={(item, children) => (
        <Link
          to={item.href}
          style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}
        >
          {children}
        </Link>
      )}
    >
      <Outlet />
    </AppShell>
  );
}
